import { getSignupAttribution } from "@/lib/attribution";
import { posthog } from "@/lib/posthog";
import { getPostHogEnrichmentSnapshot } from "@/lib/posthog-enrichment";
import Settings from "@/services/settings";

type AnalyticsProperties = Record<string, unknown>;

export { IA_CREDIT_OPERATIONAL_ALERTS } from "@/lib/ia-credit-operational-analytics";

function getDeviceType(): "desktop" | "mobile" | "tablet" {
  if (typeof window === "undefined") return "desktop";
  const w = window.innerWidth;
  if (w < 768) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getStandardProps(): AnalyticsProperties {
  if (typeof window === "undefined") return {};
  const enriched = getPostHogEnrichmentSnapshot();
  const account = Settings.getSettings()?.accountsDetails?.[0];
  const isB2b = account?.signupProfile === "school";
  return {
    account_type: isB2b ? "b2b" : "b2c",
    school_id: isB2b ? (account?._id ?? null) : null,
    app_version: process.env.NEXT_PUBLIC_APP_VERSION ?? null,
    app_section: "manager_b2c",
    device_type: getDeviceType(),
    is_nmg: enriched.is_nmg ?? Boolean(account?.["new-manager-global"]),
    persona: enriched.persona ?? null,
    effective_plan_key: enriched.effective_plan_key ?? enriched.signup_plan_key ?? null,
    product_line: enriched.product_line ?? null,
  };
}

function getUtmProps(): AnalyticsProperties {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") ?? undefined,
    utm_campaign: params.get("utm_campaign") ?? undefined,
    utm_medium: params.get("utm_medium") ?? undefined,
    referrer: document.referrer || undefined,
  };
}

function safeCapture(event: string, properties?: AnalyticsProperties): void {
  if (typeof window === "undefined") return;
  try {
    posthog.capture(event, { ...getStandardProps(), ...properties });
  } catch {
    // Don't break user flow if PostHog fails.
  }
}

function trackOnce(storageKey: string, event: string, properties?: AnalyticsProperties): void {
  if (typeof window === "undefined") return;
  try {
    if (localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, "1");
    safeCapture(event, properties);
  } catch {
    safeCapture(event, properties);
  }
}

// ─── Auth — P0 ───────────────────────────────────────────────────────────────

export const Analytics = {
  // Signup
  signupStarted(properties?: AnalyticsProperties) {
    safeCapture("signup_started", properties);
  },

  signupCompleted(properties?: AnalyticsProperties) {
    safeCapture("signup_completed", {
      ...getUtmProps(),
      ...getSignupAttribution(),
      language: typeof navigator !== "undefined" ? navigator.language : undefined,
      ...properties,
    });
  },

  signupFailed(properties: { signup_method: string; error_code: string; error_message?: string }) {
    safeCapture("signup_failed", properties);
  },

  // Login
  loginCompleted(properties: { login_method: string; is_returning?: boolean }) {
    safeCapture("login_completed", properties);
  },

  loginFailed(properties: { login_method: string; error_code: string }) {
    safeCapture("login_failed", properties);
  },

  // ─── Activation — P1 ───────────────────────────────────────────────────────

  firstActivityCreated(properties: {
    activity_type: "quiz" | "mission";
    creation_method: "manual" | "ai_generated" | "template";
    hours_since_signup?: number;
  }) {
    trackOnce("gamefik_first_activity_created", "first_activity_created", properties);
  },

  firstClassCreated(properties: {
    creation_method: "manual" | "imported_csv";
    student_count?: number;
    hours_since_signup?: number;
  }) {
    trackOnce("gamefik_first_class_created", "first_class_created", properties);
  },

  firstStudentAdded(properties: {
    method: "manual_add" | "csv_import" | "invite_link";
    hours_since_signup?: number;
  }) {
    trackOnce("gamefik_first_student_added", "first_student_added", properties);
  },

  firstActivityPublished(properties: {
    activity_id?: string;
    student_count_target?: number;
    hours_since_signup?: number;
  }) {
    trackOnce("gamefik_first_activity_published", "first_activity_published", properties);
  },

  // ─── Recurring creation — P1 ───────────────────────────────────────────────

  activityCreated(properties: {
    activity_type: "quiz" | "mission";
    creation_method: "manual" | "ai_generated" | "template";
    question_count?: number;
    is_first_of_user?: boolean;
    source?: string;
    status?: string;
  }) {
    safeCapture("activity_created", properties);
  },

  activityPublished(properties: {
    activity_id?: string;
    class_id?: string;
    student_count_target?: number;
    publish_method?: "immediate" | "scheduled";
  }) {
    safeCapture("activity_published", properties);
  },

  classCreated(properties: {
    creation_method: "manual" | "imported_csv";
    student_count?: number;
  }) {
    safeCapture("class_created", properties);
  },

  studentAdded(properties: {
    class_id?: string;
    method: "manual_one" | "csv_import" | "invite_link_used";
    batch_size?: number;
  }) {
    safeCapture("student_added", properties);
  },

  // ─── Legacy (kept for backwards compat, now delegates to activityCreated) ──

  quizCreated(properties?: AnalyticsProperties) {
    safeCapture("quiz_created", properties);
    this.activityCreated({
      activity_type: "quiz",
      creation_method: (properties?.creation_method as "ai_generated" | "manual") ?? "ai_generated",
      source: properties?.source as string | undefined,
      status: properties?.status as string | undefined,
    });
  },

  missionCreated(properties?: AnalyticsProperties) {
    safeCapture("mission_created", properties);
    this.activityCreated({
      activity_type: "mission",
      creation_method: (properties?.creation_method as "ai_generated" | "manual") ?? "ai_generated",
      source: properties?.source as string | undefined,
      status: properties?.status as string | undefined,
    });
  },

  importClassesCompleted(properties?: AnalyticsProperties) {
    safeCapture("import_classes_completed", properties);
  },

  // ─── Chat IA — P2 ──────────────────────────────────────────────────────────

  chatIaOpened(properties: { entry_point: "menu" | "cta_dashboard" | "cta_class_empty" | "landing_page" | "direct" }) {
    safeCapture("chat_ia_opened", properties);
  },

  chatIaPromptSent(properties: {
    prompt_length: number;
    is_template?: boolean;
    prompt_language?: string;
  }) {
    safeCapture("chat_ia_prompt_sent", properties);
  },

  chatIaActivityGenerated(properties: {
    generation_duration_seconds: number;
    activity_type: "quiz" | "missao" | "flashcard";
    question_count?: number;
  }) {
    safeCapture("chat_ia_activity_generated", properties);
  },

  chatIaActivityAccepted(properties: {
    activity_type: "quiz" | "missao" | "flashcard";
    /** "trail" = handoff para o editor de trilhas (flashcards publicam via trilha). */
    publish_mode: "published" | "draft" | "trail";
  }) {
    safeCapture("chat_ia_activity_accepted", properties);
  },

  // ─── Billing — P2 ──────────────────────────────────────────────────────────

  plansPageViewed(properties?: { current_plan?: string }) {
    safeCapture("plans_page_viewed", properties);
  },

  planUpgradeStarted(properties: {
    from_plan: string;
    to_plan: string;
    trigger?: "limit_reached" | "cta_dashboard" | "banner" | "direct";
  }) {
    safeCapture("plan_upgrade_started", properties);
  },

  planUpgradeCompleted(properties: {
    to_plan: string;
    payment_method?: string;
    trial_used?: boolean;
  }) {
    safeCapture("plan_upgrade_completed", properties);
  },

  // ─── Quality — P3 ──────────────────────────────────────────────────────────

  errorEncountered(properties: {
    error_code: string;
    error_message?: string;
    context?: string;
  }) {
    safeCapture("error_encountered", properties);
  },

  featureBlockedByPlan(properties: {
    feature: string;
    current_plan?: string;
    limit_value?: number;
  }) {
    safeCapture("feature_blocked_by_plan", properties);
  },

  // ─── Onboarding (B2B only) ─────────────────────────────────────────────────

  onboardingStepCompleted(properties?: AnalyticsProperties) {
    safeCapture("onboarding_step_completed", properties);
  },

  onboardingCompleted(properties?: AnalyticsProperties) {
    safeCapture("onboarding_completed", properties);
  },

  // ─── Teacher Onboarding ────────────────────────────────────────────────────

  teacherOnboardingStarted(properties?: AnalyticsProperties) {
    safeCapture("teacher_onboarding_started", properties);
  },

  teacherOnboardingStepCompleted(properties?: AnalyticsProperties) {
    safeCapture("teacher_onboarding_step_completed", properties);
  },

  teacherOnboardingActivitySubmitted(properties?: AnalyticsProperties) {
    safeCapture("teacher_onboarding_activity_submitted", properties);
  },

  teacherOnboardingCompleted(properties?: AnalyticsProperties) {
    safeCapture("teacher_onboarding_completed", properties);
  },

  teacherOnboardingAbandoned(properties?: AnalyticsProperties) {
    safeCapture("teacher_onboarding_abandoned", properties);
  },

  teacherOnboardingSubjectSelected(properties?: AnalyticsProperties) {
    safeCapture("teacher_onboarding_subject_selected", properties);
  },

  teacherOnboardingStudentPreviewShown(properties?: AnalyticsProperties) {
    safeCapture("teacher_onboarding_student_preview_shown", properties);
  },

  teacherOnboardingPublishedFromCelebration(properties?: AnalyticsProperties) {
    safeCapture("teacher_onboarding_published_from_celebration", properties);
  },

  // ─── Billing legacy ────────────────────────────────────────────────────────

  trialStarted(properties?: AnalyticsProperties) {
    safeCapture("trial_started", properties);
  },

  planSelected(properties?: AnalyticsProperties) {
    safeCapture("plan_selected", properties);
  },

  checkoutStarted(properties?: AnalyticsProperties) {
    safeCapture("checkout_started", properties);
  },

  checkoutCompleted(properties?: AnalyticsProperties) {
    safeCapture("checkout_completed", properties);
  },

  // ─── Home ──────────────────────────────────────────────────────────────────

  homeViewed(properties: { state: "new" | "activated"; completed_steps: number; total_steps: number }) {
    safeCapture("home_viewed", properties);
  },

  homeChatBarExpanded(properties?: { source: "focus" | "type_card"; type?: string }) {
    safeCapture("home_chatbar_expanded", properties);
  },

  homeTypeCardClicked(properties: { type: string }) {
    safeCapture("home_type_card_clicked", properties);
  },

  homeTrailBannerClicked() {
    safeCapture("home_trail_banner_clicked");
  },

  homeChecklistStepCta(properties: { step_id: string }) {
    safeCapture("home_checklist_step_cta", properties);
  },

  homeChecklistStepCompleted(properties: { step_id: string; completed_steps: number; total_steps: number }) {
    safeCapture("home_checklist_step_completed", properties);
  },

  homeChecklistStepVideoOpened(properties: { step_id: string }) {
    safeCapture("home_checklist_step_video_opened", properties);
  },

  homeChecklistDismissed(properties: { completed_steps: number }) {
    safeCapture("home_checklist_dismissed", properties);
  },

  homeChecklistDone() {
    safeCapture("home_checklist_done");
  },

  homeCelebrationShown(properties: { started_players: number }) {
    safeCapture("home_celebration_shown", properties);
  },

  homeCelebrationCtaClicked() {
    safeCapture("home_celebration_cta_clicked");
  },

  // ─── Home · Liberte os Heróis ──────────────────────────────────────────────

  homeHeroesImageClicked() {
    safeCapture("home_heroes_image_clicked");
  },

  homeHeroesModalStepClicked(properties: { step_id: string }) {
    safeCapture("home_heroes_modal_step_clicked", properties);
  },

  homeHeroesCouponCopied() {
    safeCapture("home_heroes_coupon_copied");
  },

  homeHeroesCelebrationShown() {
    safeCapture("home_heroes_celebration_shown");
  },

  homeHeroesRewardModalOpened() {
    safeCapture("home_heroes_reward_modal_opened");
  },

  homeHeroesRewardClaimed(properties: { via: "copy" | "cta" }) {
    safeCapture("home_heroes_reward_claimed", properties);
  },

  homeHeroesRewardExpired() {
    safeCapture("home_heroes_reward_expired");
  },

  homeRecentDraftContinueClicked() {
    safeCapture("home_recent_draft_continue_clicked");
  },

  homeRecentPendingProofsClicked(properties: { count: number }) {
    safeCapture("home_recent_pending_proofs_clicked", properties);
  },

  homeRecentActivityClicked(properties: { type: string; response_count: number }) {
    safeCapture("home_recent_activity_clicked", properties);
  },

  // ─── Primeiros passos · superfícies globais (topbar/toast/empty states) ────

  firstStepsTopbarShown(properties: { variant: string; state: string }) {
    safeCapture("first_steps_topbar_shown", properties);
  },

  firstStepsTopbarOpened(properties: { variant: string; completed_steps: number }) {
    safeCapture("first_steps_topbar_opened", properties);
  },

  firstStepsTopbarStepClicked(properties: { step_id: string; variant: string }) {
    safeCapture("first_steps_topbar_step_clicked", properties);
  },

  firstStepsTopbarCtaClicked(properties: { step_id: string; variant: string }) {
    safeCapture("first_steps_topbar_cta_clicked", properties);
  },

  firstStepsToastShown(properties: { step_id: string; variant: string }) {
    safeCapture("first_steps_toast_shown", properties);
  },

  firstStepsToastClicked(properties: { step_id: string; variant: string }) {
    safeCapture("first_steps_toast_clicked", properties);
  },

  firstStepsEmptyStateShown(properties: { step_id: string; screen: string; variant: string }) {
    safeCapture("first_steps_empty_state_shown", properties);
  },

  firstStepsEmptyStateClicked(properties: { step_id: string; screen: string; variant: string }) {
    safeCapture("first_steps_empty_state_clicked", properties);
  },

  firstStepsRewardStateShown(properties: { state: string; variant: string }) {
    safeCapture("first_steps_reward_state_shown", properties);
  },

  custom(event: string, properties?: AnalyticsProperties) {
    safeCapture(event, properties);
  },
};
